import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, FileText, Shield, Zap, ArrowRight, Sparkles, Users, ClipboardList } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const forms = [
  { title: "Surat Kuasa Pengambilan Paspor RI", desc: "Titipkan pengambilan paspor Anda kepada pihak terpercaya." },
  { title: "Surat Permohonan Paspor Lama (Dewasa)", desc: "Memohon paspor lama Anda kembali sebagai arsip pribadi." },
  { title: "Surat Permohonan Paspor Lama (Anak)", desc: "Untuk orang tua/wali yang memohon paspor lama anak." },
  { title: "Surat Kuasa dan Pernyataan Orang Tua", desc: "Beri kuasa pengurusan dokumen perjalanan anak." },
];

const benefits = [
  { icon: Zap, title: "Isi sekali, pakai semua", desc: "Data biodata Anda otomatis mengisi seluruh formulir. Tidak perlu tulis ulang." },
  { icon: Shield, title: "Rapi & Bebas Salah Tulis", desc: "Tulisan tangan yang sulit dibaca kini digantikan formulir ketik dengan validasi." },
  { icon: FileText, title: "PDF Siap Cetak A4", desc: "Layout identik dengan formulir resmi kantor imigrasi, siap tanda tangan & materai." },
  { icon: Users, title: "Ramah Semua Pengguna", desc: "Antarmuka sederhana yang mudah dipahami pemohon maupun petugas." },
];

const steps = [
  { n: "01", t: "Pilih Formulir", d: "Tentukan surat yang Anda butuhkan dari daftar formulir resmi." },
  { n: "02", t: "Isi Data Sekali", d: "Masukkan biodata Anda satu kali — sistem menyimpan untuk formulir lainnya." },
  { n: "03", t: "Unduh & Cetak", d: "Preview PDF, unduh, cetak di atas kertas A4, tanda tangani dengan materai." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="public" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-gold-400/50 bg-gold-500/10 px-3 py-1 text-xs font-semibold text-gold-600 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Digital pertama untuk formulir paspor
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
              Formulir paspor,{" "}
              <span className="bg-gradient-to-r from-blue-700 via-blue-600 to-gold-500 bg-clip-text text-transparent">tanpa pulpen</span>.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              SIMPA (Sistem Mandiri Paspor) menggantikan formulir tulis tangan menjadi pengisian digital yang cepat, rapi,
              dan bebas salah. Cukup isi data satu kali — semua formulir Anda siap dicetak dalam hitungan menit.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-lift rounded-full bg-blue-600 hover:bg-blue-700 text-white h-12 px-6" data-testid="hero-start-btn">
                <Link to="/pemohon">Mulai Isi Formulir <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full h-12 px-6" data-testid="hero-learn-btn">
                <a href="#cara-pakai">Lihat Cara Pakai</a>
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Tanpa buat akun</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Gratis</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Data tersimpan lokal</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border border-slate-200">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/4b4a7cd4-6731-4d2d-afb4-9f61ad6dfd27/images/d1f96d265a6a9a1ba980805dd772cb7ec942075d3eecaf107aaf5541bf8e7f58.jpeg"
                alt="Warga Indonesia memegang Paspor Republik Indonesia"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-slate-200 p-4 max-w-xs hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">4 Formulir Siap</div>
                  <div className="text-xs text-slate-500">Kantor Imigrasi Kelas II TPI Belu</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="border-t border-slate-100 bg-slate-50/50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Manfaat</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Dibuat agar antrean Anda lebih cepat.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-md hover:border-blue-200 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 grid place-items-center mb-4">
                  <b.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900 tracking-tight">{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CARA PAKAI */}
      <section id="cara-pakai" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Cara Pakai</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Tiga langkah — selesai dalam 5 menit.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Sederhana, cepat, dan bebas ribet. Dirancang untuk semua kalangan.</p>
            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 aspect-video">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/4b4a7cd4-6731-4d2d-afb4-9f61ad6dfd27/images/87cb23d49394801082b47fb6699a70ba8b96c13da58bf8ba27ff0a83b16dd719.jpeg"
                alt="Warga Indonesia mengisi formulir digital SIMPA"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-2xl border border-slate-200 bg-white p-6 flex gap-5 items-start hover:border-blue-300 transition-colors"
              >
                <div className="text-3xl font-bold text-blue-600 font-[Outfit]">{s.n}</div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg tracking-tight">{s.t}</h3>
                  <p className="mt-1 text-slate-600 leading-relaxed">{s.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMS LIST */}
      <section className="border-t border-slate-100 bg-slate-50/50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Formulir Tersedia</div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Empat formulir yang paling sering dibutuhkan.</h2>
            </div>
            <Button asChild variant="outline" className="rounded-full" data-testid="see-all-forms-btn">
              <Link to="/pemohon">Lihat Semua <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {forms.map((f) => (
              <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 grid place-items-center flex-shrink-0">
                    <ClipboardList className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 tracking-tight">{f.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold">Pertanyaan Umum</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Kami menjawab pertanyaan Anda.</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            <AccordionItem value="q1">
              <AccordionTrigger className="text-left">Apakah saya perlu membuat akun?</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Tidak. SIMPA dirancang untuk digunakan langsung oleh pemohon tanpa registrasi. Data biodata Anda hanya disimpan di perangkat Anda sendiri (browser).
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q2">
              <AccordionTrigger className="text-left">Apakah PDF hasilnya berlaku resmi?</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Ya, layout PDF meniru formulir resmi Kantor Imigrasi. Setelah dicetak di kertas A4 dan ditandatangani di atas materai Rp 10.000, formulir siap diserahkan.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q3">
              <AccordionTrigger className="text-left">Bagaimana dengan keamanan data saya?</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Data biodata Anda disimpan lokal di browser. Hanya saat Anda menekan tombol "Selesai & Cetak", data akan diproses menjadi PDF di server dan disimpan pada arsip petugas.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q4">
              <AccordionTrigger className="text-left">Bisakah saya edit ulang setelah pratinjau?</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Bisa. Setiap langkah di wizard bisa Anda kembali dan ubah. Preview PDF juga dapat direvisi sebelum diunduh.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-blue-600 p-10 sm:p-14 text-white overflow-hidden relative">
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-gold-500/20" />
            <div className="absolute -right-32 -bottom-24 w-80 h-80 rounded-full bg-white/5" />
            <img src="/logo-mark.png" alt="" aria-hidden className="absolute right-10 top-1/2 -translate-y-1/2 h-44 w-auto opacity-[0.18] pointer-events-none hidden md:block" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Siap mengurus paspor tanpa antre lama?</h2>
              <p className="mt-3 text-blue-50 max-w-xl leading-relaxed">Isi formulir Anda sekarang. Cepat, digital, dan siap cetak.</p>
              <Button asChild size="lg" className="mt-8 btn-lift rounded-full bg-white text-blue-600 hover:bg-blue-50 h-12 px-6" data-testid="cta-start-btn">
                <Link to="/pemohon">Mulai Sekarang <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="SIMPA" className="h-8 w-auto" />
            <div className="text-sm text-slate-500">© 2026 SIMPA — Sistem Mandiri Paspor. Kantor Imigrasi Kelas II TPI Belu.</div>
          </div>
          <Link to="/login" className="text-sm text-slate-600 hover:text-blue-600" data-testid="footer-login-link">Login Petugas / Admin</Link>
        </div>
      </footer>
    </div>
  );
}
